/* eslint-disable react-hooks/exhaustive-deps */
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { DateTime } from 'luxon'

import { useAuthentication } from '@hooks/useAuthentication'
import { TimeRangeEnum, timeRangeValue } from '@interfaces/TimeRangeEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { useMyWritingAction } from '@hooks/book/myWriting/useMyWritingAction'
import { SaleAndAnalyticsQueryParams } from '../SaleAnalyticsTab/interfaces/SaleAndAnalyticsQueryParams'
import { EbookSaleAnalyticsOverviewProps } from './interface'
import { SearchSaleAnalyticsType } from '../SaleAnalyticsTab/components/SearchSaleAnalytics/interfaces/SearchSaleAnalyticsType'

export function withEbookSaleAnalyticsOverview(
  Component: React.FC<EbookSaleAnalyticsOverviewProps>
) {
  function WithEbookSaleAnalyticsOverview() {
    const myWritingClient = useMyWritingAction()
    const router = useRouter()
    const advanceSearch = router.query.advanceSearch === 'true'
    const { user, isAuthenticated, isWriter } = useAuthentication()
    const currentTime = DateTime.now()
    const [queryParams, setQueryParams] = useState<SaleAndAnalyticsQueryParams>(
      {
        ebookId: undefined,
        bookTypes: undefined,
        categoryIds: undefined,
        penNameIds: undefined,
        startDate: currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
          .startOf('day'),
        endDate: currentTime.endOf('day'),
      }
    )

    const queryKey = ['sales-analytics-overview', 'ebook', user.id, queryParams]

    const { data, isLoading } = useQuery(
      queryKey,
      () => myWritingClient.getWriterSalesAnalyticsEbookOverview(queryParams),
      {
        enabled:
          !!isAuthenticated && isWriter && router.query.subtab === 'ebook',
      }
    )

    const handleSearch = async ({
      ebookId,
      categoryIds,
      penNameId,
      bookType,
      startAt,
      endAt,
      timeRange,
    }: SearchSaleAnalyticsType) => {
      if (!timeRange && (!startAt || !endAt)) return

      const query: any = {
        tab: 'salesAnalytics',
        advanceSearch,
        subtab: 'ebook',
      }
      if (timeRange) query.timeRange = timeRange
      if (startAt) query.startDate = startAt.toString()
      if (endAt) query.endDate = endAt.toString()
      if (ebookId) query.ebookId = ebookId
      if (categoryIds.length) query.categoryIds = categoryIds
      if (penNameId.length) query.penNameIds = penNameId
      if (bookType.length) query.bookTypes = bookType

      router.replace(
        {
          pathname: '/my-writing',
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    useEffect(() => {
      let newStartAt = router.query.startDate
        ? DateTime.fromJSDate(new Date(router.query.startDate as string))
        : currentTime
            .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
            .startOf('day')
      let newEndAt = router.query.endDate
        ? DateTime.fromJSDate(new Date(router.query.endDate as string)).endOf(
            'day'
          )
        : currentTime.endOf('day')

      if (router.query.timeRange === TimeRangeEnum.DAY) {
        newStartAt = currentTime.startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.WEEK) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.WEEK] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.THREE_MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.THREE_MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.SIX_MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.SIX_MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.YEAR) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.YEAR] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      }

      const bookTypes = router.query.bookTypes
        ? typeof router.query.bookTypes === 'string'
          ? ([router.query.bookTypes] as BookEnum[])
          : (router.query.bookTypes as BookEnum[])
        : undefined

      const penNameIds = router.query.penNameIds
        ? typeof router.query.penNameIds === 'string'
          ? [router.query.penNameIds]
          : router.query.penNameIds
        : undefined

      const categoryIds = router.query.categoryIds
        ? typeof router.query.categoryIds === 'string'
          ? [router.query.categoryIds]
          : router.query.categoryIds
        : undefined

      if (newStartAt && newEndAt) {
        setQueryParams({
          ebookId: Number(router.query.ebookId) || undefined,
          categoryIds,
          penNameIds,
          bookTypes,
          startDate: newStartAt,
          endDate: newEndAt,
        })
      }
    }, [
      router.query.ebookId,
      router.query.bookTypes,
      router.query.categoryIds,
      router.query.penNameIds,
      router.query.startDate,
      router.query.endDate,
      router.query.timeRange,
    ])
    const newProps = {
      initialValues: {
        ebookId: queryParams.ebookId,
        bookType: queryParams.bookTypes || [],
        categoryIds: queryParams.categoryIds || [],
        penNameId: queryParams.penNameIds || [],
        timeRange:
          router.query.startDate && router.query.endDate
            ? undefined
            : (router.query.timeRange as TimeRangeEnum) || TimeRangeEnum.MONTH,
        startAt: router.query.startDate
          ? queryParams.startDate.toJSDate()
          : undefined,
        endAt: router.query.endDate
          ? queryParams.endDate.toJSDate()
          : undefined,
      },
      data,
      isLoading,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
      queryParams,
      handleSearch,
    }
    return <Component {...newProps} />
  }
  return WithEbookSaleAnalyticsOverview
}
